import React from 'react'

import { Col, Flex, Spacer } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { EmailContentItemDTO } from 'src/models/dto/items/EmailContentItemDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import {
    ItemEditorTextArea,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { EmailContentHandler } from 'src/services/EntityServices/ItemUpdateHandlers/EmailContentHandler'

export const EmailContentItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<EmailContentItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    const { matches } = useBreakpoints()

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <ItemEditorBasicInfo
                handler={EmailContentHandler}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
            />
            <Spacer height='S400' />
            <Flex
                flexDirection={matches.s ? 'column' : 'row'}
                width='100%'
                gridGap='S300'
                alignItems={'flex-start'}
                flexWrap={'wrap'}
            >
                <Col flex='1 1' width={matches.s ? '100%' : undefined}>
                    <ItemEditorTextInput
                        dataTestId={'email-from'}
                        itemId={itemDTO.id}
                        inputId='email-from'
                        labelText='From (Email sender)'
                        placeholder='Enter an email address'
                        disabled={editDisabled}
                        value={itemDTO.fromI18N[locale] || ''}
                        setValue={(nextValue: string) =>
                            EmailContentHandler.updateFrom(itemDTO.id, locale, nextValue)
                        }
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.fromI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                    />
                </Col>
                <Col flex='1 1' width={matches.s ? '100%' : undefined}>
                    <ItemEditorTextInput
                        dataTestId={'email-to'}
                        itemId={itemDTO.id}
                        inputId='email-to'
                        labelText='To (Email recipient)'
                        placeholder='Enter an email address'
                        value={itemDTO.toI18N[locale] || ''}
                        disabled={editDisabled}
                        setValue={(nextValue: string) =>
                            EmailContentHandler.updateTo(itemDTO.id, locale, nextValue)
                        }
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.toI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                    />
                </Col>
            </Flex>
            <Spacer height='S400' />
            <Col width={'100%'}>
                <ItemEditorTextInput
                    dataTestId={'email-subject'}
                    itemId={itemDTO.id}
                    inputId='email-subject'
                    labelText='Subject line'
                    placeholder='Write a subject line'
                    value={itemDTO.subjectI18N[locale] || ''}
                    disabled={editDisabled}
                    setValue={(nextValue: string) =>
                        EmailContentHandler.updateSubject(itemDTO.id, locale, nextValue)
                    }
                    validationErrorMessage={(
                        validationErrorEntity.validationErrors.subjectI18N ?? []
                    ).join(', ')}
                    showError={showValidationError}
                />
            </Col>
            <Spacer height='S400' />
            <Col flex='1'>
                <ItemEditorTextArea
                    dataTestId={'email-message'}
                    inputId={'email-message'}
                    locale={locale}
                    value={itemDTO.messageI18N[locale] ?? ''}
                    disabled={editDisabled}
                    itemId={itemDTO.id}
                    setValue={(nextValue: string) => {
                        EmailContentHandler.updateMessage(itemDTO.id, locale, nextValue)
                    }}
                    validationErrorMessage={(
                        validationErrorEntity.validationErrors.messageI18N ?? []
                    ).join(', ')}
                    showError={showValidationError}
                    labelText={'Email message'}
                />
            </Col>
        </>
    )
}
